import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  useGetDatasetDataMutation,
  useMutationDatasetMetadata,
  useMutationPatchDatasetSchema
} from "@/api/dataset/useDatasetAPI";
import {
  DatasetDetailRow,
  ExtendedColumnDef,
  generateColumns
} from "@/components/datasetManagement/details/columns";
import {
  DatasetAttributeSchema,
  DatasetData,
  DatasetMetadata,
  DatasetSchemaPayload
} from "@/types/dataset";

import { useDatasetStream } from "../useDatasetStream";

export interface IUseDetailDataset {
  schema?: DatasetAttributeSchema[];
  dynamicColumns: (
    handleEditRow: (row: DatasetDetailRow) => void,
    handleDeleteRows: (rowIndex: string) => void,
    isRowDatasetManagementEnabled: boolean,
    isEditDataGranted?: boolean,
    isDeleteDataGranted?: boolean
  ) => ExtendedColumnDef<DatasetDetailRow, unknown>[];
  metadata?: DatasetMetadata;
  data: DatasetDetailRow[];
  isLoading: boolean;
  datasetData?: DatasetData;
  refetchDatasetDetail: () => void;
  refetchDatasetData: () => void;
}

const convertDatasetDataToTableData = (
  dataset?: DatasetData
): DatasetDetailRow[] => {
  if (!dataset?.rows || !dataset.columns) return [];

  return dataset.rows.map((row) => {
    const tableRow: DatasetDetailRow = {};
    row.forEach((value, index) => {
      const column = dataset.columns[index];

      if (column) tableRow[column.name] = value;
    });

    return tableRow;
  });
};

export const useDetailDataset = (): IUseDetailDataset => {
  const { id } = useParams();

  const datasetId = id ?? "";
  const [isStreamLoading, setIsStreamLoading] = useState(false);
  const [datasetsData, setDatasetsData] = useState<DatasetData | undefined>();
  const [datasetsMetadata, setDatasetsMetadata] = useState<
    DatasetMetadata | undefined
  >();

  useDatasetStream({
    datasetId: id,
    enabled: !!id,
    onOpen: () => {
      setIsStreamLoading(true);
    },
    onStreamComplete: (data) => {
      setDatasetsMetadata(data.datasetMetadata);
      setDatasetsData(data.rawDataset);

      setIsStreamLoading(false);
    },
    onError: () => {
      setIsStreamLoading(false);
    }
  });

  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata({
    onSuccess: (data) => {
      setDatasetsMetadata(data);
    }
  });
  const { mutateAsync: getDatasetDataPost } = useGetDatasetDataMutation({
    options: {
      onSuccess: (data) => {
        setDatasetsData(data);
      }
    }
  });

  const refetchDatasetDetail = () => {
    getDatasetMetadata({ id: datasetId, h3Configuration: null });
  };
  const refetchDatasetData = () => {
    getDatasetDataPost({ id: datasetId });
  };

  const { mutate: mutatePatchDatasetSchema } = useMutationPatchDatasetSchema({
    onMutate: async (payload: DatasetSchemaPayload) => {
      if (datasetsMetadata) {
        setDatasetsMetadata({
          ...datasetsMetadata,
          schema: datasetsMetadata?.schema.map((schema) =>
            schema.id === payload.id ? payload : { ...schema }
          ) as DatasetAttributeSchema[]
        });
      }

      return { previousMetadata: datasetsMetadata };
    },
    onError: (_err, _payload, context) => {
      if (context?.previousMetadata) {
        // Revert to previous metadata if an error occurs
        setDatasetsMetadata(context.previousMetadata);
      }
    },
    onSuccess: () => {
      // Refetch dataset detail only on success
      refetchDatasetDetail();
    }
  });

  const dynamicColumns = (
    handleEditRow: (row: DatasetDetailRow) => void,
    handleDeleteRows: (rowIndex: string) => void,
    isRowDatasetManagementEnabled: boolean,
    isEditDataGranted: boolean = false,
    isDeleteDataGranted: boolean = false
  ) => {
    return datasetsMetadata?.schema
      ? generateColumns({
          columnsConfig: datasetsMetadata.schema,
          published: datasetsMetadata.published,
          mutatePatchDatasetSchema,
          handleEditRow,
          handleDeleteRows,
          isRowDatasetManagementEnabled,
          isEditDataGranted,
          isDeleteDataGranted
        })
      : [];
  };

  const data = useMemo(
    () => convertDatasetDataToTableData(datasetsData),
    [datasetsData]
  );

  return {
    schema: datasetsMetadata?.schema,
    dynamicColumns,
    metadata: datasetsMetadata,
    data,
    datasetData: datasetsData,
    isLoading: isStreamLoading,
    refetchDatasetDetail,
    refetchDatasetData
  };
};
